import { useState, useEffect } from "react";
import { Link } from "wouter";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { FullWidthDropdown } from "./FullWidthDropdown";
import { useTheme } from "@/components/layout/theme-provider";
import { Button } from "@/components/ui/button";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faXmark, faChevronDown, faFire, faStore, faScrewdriverWrench, faPersonFalling, faHandFist, faSackDollar, faHandshakeSimple, faNetworkWired, faCrosshairs, faBriefcase, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { Lock } from 'lucide-react';
import { cn } from "@/lib/utils";
import { useTranslation } from 'react-i18next';
import React from "react";
import { ICONS } from "@/assets/icons";





interface HeaderProps {
  isLoggedIn?: boolean;
  onDashboardClick?: () => void;
}

export function Header({ isLoggedIn = false, onDashboardClick }: HeaderProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { theme, setTheme } = useTheme();
  const [language, setLanguage] = useState(localStorage.getItem('i18nextLng') || "en");
  // Desktop dropdown states (independent)
  const [isModelsOpen, setIsModelsOpen] = useState(false);
  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);

  const { i18n } = useTranslation()

  // Dropdown close timers
  const modelsTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const solutionsTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const aboutTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Mobile dropdown states
  const [isMobileModelsOpen, setIsMobileModelsOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isMobileAboutOpen, setIsMobileAboutOpen] = useState(false);

  // Handler to change language and persist in localStorage
  const handleLanguageChange = (newLang: string) => {
    setLanguage(newLang);
    localStorage.setItem('i18nextLng', newLang);
    // i18n will be handled by the LanguageSwitcher component
  }

  // Handle scrolling effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Toggle mobile menu
  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  // Toggle theme
  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };

  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full transition-all duration-300  shadow-lg backdrop-blur",
        isScrolled
          ? "bg-[#0F2030] backdrop-blur-sm"
          : "bg-[#0F2030] backdrop-blur-sm"
      )}
    >
      <nav className="max-w-8xl mx-auto px-2 sm:px-8 lg:px-12">
        <div className="flex justify-between items-center h-16 md:h-16 py-1 md:py-0">
          <div className="flex items-center">
            <Link href="/" className="flex-shrink-0">
              <img
                src="/oleyes_header_logo.png"
                alt="Oley Security"
                className="h-8 w-auto md:h-8 transition-all duration-300"
              />
            </Link>
            {/* Desktop Navigation */}
            <div className="hidden md:ml-8 md:flex md:space-x-6 items-center">


              {/* Models Dropdown (independent) */}
              <FullWidthDropdown
                open={isModelsOpen}
                setOpen={setIsModelsOpen}
                timersRef={modelsTimeout}
                topOffsetClass="top-[45px]"
                triggerClassName={cn(
                  isModelsOpen ? "border-primary text-primary" : "border-transparent text-white"
                )}
                panel={
                  <ul className="space-y-1">
                    <li>
                      <a href="/fire-model-details" className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors text-white text-sm">
                        <FontAwesomeIcon icon={faFire} className="h-4 w-4 text-white/80" /> {t('header.fire_smoke')}
                      </a>
                    </li>
                    <li>
                      <a href="/fall-model-details" className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors text-white text-sm">
                        <FontAwesomeIcon icon={faPersonFalling} className="h-4 w-4 text-white/80" /> {t('header.fall_detection')}
                      </a>
                    </li>
                    <li>
                      <a href="/violence-model-details" className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors text-white text-sm">
                        <FontAwesomeIcon icon={faHandFist} className="h-4 w-4 text-white/80" /> {t('header.violence_detection')}
                      </a>
                    </li>
                    <li>
                      <a href="/shoplift-model-details" className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors text-white text-sm">
                        <FontAwesomeIcon icon={faSackDollar} className="h-4 w-4 text-white/80" /> {t('header.shoplifting')}
                      </a>
                    </li>
                  </ul>
                }
              >
                {t('header.ai_models')}
                <FontAwesomeIcon icon={faChevronDown} className={`h-4 w-4 transition-transform duration-200 ${isModelsOpen ? 'rotate-180' : ''}`} />
              </FullWidthDropdown>

              {/* Solutions Dropdown (independent) */}
              <FullWidthDropdown
                open={isSolutionsOpen}
                setOpen={setIsSolutionsOpen}
                timersRef={solutionsTimeout}
                topOffsetClass="top-[45px]"
                triggerClassName={cn(
                  isSolutionsOpen ? "border-primary text-primary" : "border-transparent text-white"
                )}
                panel={
                  <div className="grid grid-cols-2 gap-8">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <FontAwesomeIcon icon={faStore} className="h-4 w-4 text-white" />
                        <h3 className="text-white text-base font-bold">{t('header.use_cases')}</h3>
                      </div>
                      <ul className="space-y-1">
                        <li>
                          <a href="/case-studies/fire-detection" className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors text-white text-sm">
                            <FontAwesomeIcon icon={faFire} className="h-4 w-4 text-white/80" />  {t('header.fire_smoke_warehouse')}
                          </a>
                        </li>
                        <li>
                          <a href="/case-studies/fall-detection" className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors text-white text-sm">
                            <FontAwesomeIcon icon={faPersonFalling} className="h-4 w-4 text-white/80" />  {t('header.fall_detection_pharmacy')}
                          </a>
                        </li>
                        <li>
                          <a href="/case-studies/violence-detection" className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors text-white text-sm">
                            <FontAwesomeIcon icon={faHandFist} className="h-4 w-4 text-white/80" />  {t('header.violence_detection_bank')}
                          </a>
                        </li>
                        <li>
                          <a href="/case-studies/shoplifting-detection" className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors text-white text-sm">
                            <FontAwesomeIcon icon={faSackDollar} className="h-4 w-4 text-white/80" />  {t('header.shoplifting_pharmacy')}
                          </a>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <FontAwesomeIcon icon={faScrewdriverWrench} className="h-5 w-5 text-white" />
                        <h3 className="text-white text-base font-bold">{t('header.integrations')}</h3>
                      </div>
                      <ul className="space-y-1">
                        <li>
                          <a href="/technology-partners" className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors text-white text-sm">
                            <FontAwesomeIcon icon={faHandshakeSimple} className="h-4 w-4 text白/80" />
                            {t('header.technology_partners')}
                          </a>
                        </li>
                        <li>
                          <a href="/gateway" className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors text-white text-sm">
                            <FontAwesomeIcon icon={faNetworkWired} className="h-4 w-4 text-white/80" />
                            {t('header.gateway')}
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                }
              >
                {t('header.solutions')}
                <FontAwesomeIcon icon={faChevronDown} className={`h-4 w-4 transition-transform duration-200 ${isSolutionsOpen ? 'rotate-180' : ''}`} />
              </FullWidthDropdown>

              {/* About */}
              <FullWidthDropdown
                open={isAboutOpen}
                setOpen={setIsAboutOpen}
                timersRef={aboutTimeout}
                topOffsetClass="top-[45px]"
                triggerClassName={cn(
                  isAboutOpen ? "border-primary text-primary" : "border-transparent text-white"
                )}
                panel={
                  <ul className="space-y-1">
                    <li>
                      <a href="/about-us" className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors text-white text-sm">
                        <FontAwesomeIcon icon={faInfoCircle} className="h-4 w-4 text-white/80" /> {t('header.about_us')}
                      </a>
                    </li>
                    <li>
                      <a href="/careers" className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors text-white text-sm">
                        <FontAwesomeIcon icon={faBriefcase} className="h-4 w-4 text-white/80" /> {t('header.careers')}
                      </a>
                    </li>
                  </ul>
                }
              >
                {t('header.about')}
                <FontAwesomeIcon icon={faChevronDown} className={`h-4 w-4 transition-transform duration-200 ${isAboutOpen ? 'rotate-180' : ''}`} />
              </FullWidthDropdown>


              {/*  */}
              {/* <a href="http://localhost:4001" className="flex items-center gap-1.5 px-2 py-1.5 text-sm font-medium text-gray-200 rounded-xl transition-all duration-300 hover:bg-white/10 hover:scale-105">
                {t('header.api')}
              </a> */}
              {/*  */}
              <a href="https://support.oleyes.com" className="flex items-center gap-1.5 px-2 py-1.5 text-sm font-medium text-gray-200 rounded-xl transition-all duration-300 hover:bg-white/10 hover:scale-105">
                {t('header.support')}
              </a>
              {/*  */}
              {/* <a href="https://billing.oleyes.com" className="flex items-center gap-1.5 px-2 py-1.5 text-sm font-medium text-gray-200 rounded-xl transition-all duration-300 hover:bg-white/10 hover:scale-105">
                {t('header.billing')}
              </a> */}
              {/*  */}
              <Link href="/contact-sales" className="flex items-center gap-1.5 px-2 py-1.5 text-sm font-medium text-gray-200 rounded-xl transition-all duration-300 hover:bg-white/10 hover:scale-105">
                {t('header.contact_sales')}
              </Link>
            </div>
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center space-x-5 ml-5">
  {/*Desktop language changer */}          
<select
  value={i18n.language}
  onChange={(e) => i18n.changeLanguage(e.target.value)}
  className="bg-[#0F2030] text-gray-200 border border-white/20 rounded-lg px-2 py-1 text-sm focus:outline-none cursor-pointer"
>
  <option value="en">EN</option>
  <option value="es">ES</option>
  <option value="fr">FR</option>
</select>
            {/* Try Now Button (White background) */}
            <Link href="/free-trial" className="group relative overflow-hidden flex-1 sm:flex-none">
              <Button>
                {t('header.try_now')}
                <span className="text-slate-900">→</span>
              </Button>
            </Link>


            {/* Login Link - Only show when not logged in */}
            {!isLoggedIn && (
              <Link
                href="/login"
                className="flex items-center gap-2 text-sm font-medium text-white/90 hover:text-white transition-colors duration-200 group"
              >
                <Lock className="h-4 w-4 text-white/80 group-hover:text-white transition-colors" />
                {t('auth.login.signIn')}
              </Link>
            )}
            {/* Dashboard Button - Only show when logged in */}
            {isLoggedIn && onDashboardClick && (
              <button
                onClick={onDashboardClick}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white border border-white/70 rounded-lg transition-all duration-300 hover:bg-white/10 hover:scale-105"
              >
                {t('header.dashboard')}
              </button>
            )}

            {/* Theme Toggle */}
            {theme === "dark" ? (
              <img src={ICONS.darkModeWhite} alt="Dark Mode" className="h-5 w-5 cursor-pointer hover:scale-110 transition-transform duration-300 text-white" onClick={toggleTheme} aria-label="Switch to light mode" />
            ) : (
              <img src={ICONS.lightModeWhite} alt="Light Mode" className="h-5 w-5 cursor-pointer hover:scale-110 transition-transform duration-300 text-white" onClick={toggleTheme} aria-label="Switch to dark mode" />
            )}

          </div>
          
          {/* Mobile menu button */}
          <div className="flex items-center md:hidden gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="text-white"
            >
              {theme === "dark" ? (
                <img src={ICONS.darkModeWhite} alt="Dark Mode" className="h-5 w-5" />
              ) : (
                <img src={ICONS.lightModeWhite} alt="Light Mode" className="h-5 w-5" />
              )}
            </Button>
            {/* Mobile Language Switcher */}
            <select
  value={i18n.language}
  onChange={(e) => i18n.changeLanguage(e.target.value)}
  className={`md:hidden ${isOpen ? 'block' : 'hidden'} bg-[#0F2030] text-gray-200 border border-white/20 rounded-lg px-2 py-1 text-sm focus:outline-none cursor-pointer`}
>
  <option value="en">EN</option>
  <option value="es">ES</option>
  <option value="fr">FR</option>
</select>
            {/* Mobile Language Switcher */}
            <div className="md:hidden">
              <LanguageSwitcher language={language} setLanguage={handleLanguageChange} />
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleMenu}
              className="text-gray-600 dark:text-gray-300"
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
            >
              {isOpen ? (
                <FontAwesomeIcon icon={faXmark} className="h-6 w-6" />
              ) : (
                <FontAwesomeIcon icon={faBars} className="h-6 w-6" />
              )}
            </Button>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      <div className={`md:hidden ${isOpen ? 'block' : 'hidden'} bg-[#0F2030] overflow-y-auto max-h-[80vh]`}>
        <div className="pt-4 pb-6 px-2 space-y-2">
          {/* Mobile Login Link - Only show when not logged in */}
          {!isLoggedIn && (
            <div className="px-4 py-3 border-b border-white/10">
              <Link
                href="/login"
                className="flex items-center justify-center gap-2 text-sm font-medium text-white/90 hover:text-white transition-colors duration-200 group"
                onClick={() => setIsOpen(false)}
              >
                <Lock className="h-4 w-4 text-white/80 group-hover:text-white transition-colors" />
                {t('auth.login.signIn')}
              </Link>
            </div>
          )}

          {/* Mobile Try Now Button */}
          <div className="px-4 py-3 border-b border-white/10">
            <Link
              href="/free-trial"
              className="w-full flex justify-center items-center gap-2 px-4 py-2 bg-white text-slate-900 rounded-lg font-medium hover:bg-gray-100 transition-colors"
              onClick={() => setIsOpen(false)}
            >
              {t('header.try_now')}
              <span>→</span>
            </Link>
          </div>
          {/*  Section (Collapsible for Mobile) */}
          <div className="px-2 py-2">
            <button
              className="w-full flex justify-between items-center text-sm font-normal text-white tracking-wide focus:outline-none"
              onClick={() => setIsMobileModelsOpen((open) => !open)}
              type="button"
            >
              <span>{t('header.ai_models')}</span>
              <FontAwesomeIcon icon={faChevronDown} className={`h-4 w-4 transition-transform ${isMobileModelsOpen ? 'rotate-180' : ''} text-white`} />
            </button>
            {isMobileModelsOpen && (
              <div className="mt-2 space-y-1">
                <a href="/fire-model-details" className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors text-white text-sm" onClick={() => setIsOpen(false)}>
                  <FontAwesomeIcon icon={faFire} className="h-4 w-4 text-white/80" /> {t('header.fire_smoke')}
                </a>
                <a href="/fall-model-details" className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors text-white text-sm" onClick={() => setIsOpen(false)}>
                  <FontAwesomeIcon icon={faPersonFalling} className="h-4 w-4 text-white/80" /> {t('header.fall_detection')}
                </a>
                <a href="/violence-model-details" className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors text-white text-sm" onClick={() => setIsOpen(false)}>
                  <FontAwesomeIcon icon={faHandFist} className="h-4 w-4 text-white/80" /> {t('header.violence_detection')}
                </a>
                <a href="/shoplift-model-details" className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors text-white text-sm" onClick={() => setIsOpen(false)}>
                  <FontAwesomeIcon icon={faSackDollar} className="h-4 w-4 text-white/80" /> {t('header.shoplifting')}
                </a>
              </div>
            )}
          </div>

          {/*  Section (Collapsible for Mobile) */}
          <div className="px-2 py-2">
            <button
              className="w-full flex justify-between items-center text-sm font-normal text-white tracking-wide focus:outline-none"
              onClick={() => setIsMobileOpen((open) => !open)}
              type="button"
            >
              <span>{t('header.solutions')}</span>
              <FontAwesomeIcon icon={faChevronDown} className={`h-4 w-4 transition-transform ${isMobileOpen ? 'rotate-180' : ''} text-white`} />
            </button>
            {isMobileOpen && (
              <div className="mt-2 space-y-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <FontAwesomeIcon icon={faCrosshairs} className="h-4 w-4 text-white" />
                    <h3 className="text-white text-base text-sm font-bold">{t('header.use_cases')}</h3>
                  </div>
                  <ul className="space-y-1">
                    <li>
                      <a href="/case-studies/fire-detection" className="flex items-center gap-2 px-3 py-2 rounded-lg dark:hover:bg-white/5 hover:bg-black/5 transition-colors text-white text-xs font-normal" onClick={() => setIsOpen(false)}>
                        <FontAwesomeIcon icon={faFire} className="h-4 w-4 text-white" />  {t('header.fire_smoke_warehouse')}
                      </a>
                    </li>
                    <li>
                      <a href="/case-studies/fall-detection" className="flex items-center gap-2 px-3 py-2 rounded-lg dark:hover:bg-white/5 hover:bg-black/5 transition-colors text-white text-xs font-normal" onClick={() => setIsOpen(false)}>
                        <FontAwesomeIcon icon={faPersonFalling} className="h-4 w-4 text-white" />  {t('header.fall_detection_pharmacy')}
                      </a>
                    </li>
                    <li>
                      <a href="/case-studies/violence-detection" className="flex items-center gap-2 px-3 py-2 rounded-lg dark:hover:bg-white/5 hover:bg-black/5 transition-colors text-white text-xs font-normal" onClick={() => setIsOpen(false)}>
                        <FontAwesomeIcon icon={faHandFist} className="h-4 w-4 text-white" />  {t('header.violence_detection_bank')}
                      </a>
                    </li>
                    <li>
                      <a href="/case-studies/shoplifting-detection" className="flex items-center gap-2 px-3 py-2 rounded-lg dark:hover:bg-white/5 hover:bg-black/5 transition-colors text-white text-xs font-normal" onClick={() => setIsOpen(false)}>
                        <FontAwesomeIcon icon={faSackDollar} className="h-4 w-4 text-white" />  {t('header.shoplifting_pharmacy')}
                      </a>
                    </li>
                  </ul>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <FontAwesomeIcon icon={faScrewdriverWrench} className="h-4 w-4 text-white" />
                    <h3 className="text-white text-base font-bold">{t('header.integrations')}</h3>
                  </div>
                  <ul className="space-y-1">
                    <li>
                      <a href="/technology-partners" className="flex items-center gap-2 px-3 py-2 rounded-lg dark:hover:bg-white/5 hover:bg-black/5 transition-colors text-white text-xs font-normal" onClick={() => setIsOpen(false)}>
                        <FontAwesomeIcon icon={faHandshakeSimple} className="h-4 w-4 text-white" />
                        {t('header.technology_partners')}
                      </a>
                    </li>
                    <li>
                      <a href="/gateway" className="flex items-center gap-2 px-3 py-2 rounded-lg dark:hover:bg-white/5 hover:bg-black/5 transition-colors text-white text-xs font-normal" onClick={() => setIsOpen(false)}>
                        <FontAwesomeIcon icon={faNetworkWired} className="h-4 w-4 text-white" />
                        {t('header.gateway')}
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* About Section (Collapsible for Mobile) */}
          <div className="px-2 py-2">
            <button
              className="w-full flex justify-between items-center text-sm font-normal text-white tracking-wide focus:outline-none"
              onClick={() => setIsMobileAboutOpen((open) => !open)}
              type="button"
            >
              <span>{t('header.about')}</span>
              <FontAwesomeIcon icon={faChevronDown} className={`h-4 w-4 transition-transform ${isMobileAboutOpen ? 'rotate-180' : ''} text-white`} />
            </button>
            {isMobileAboutOpen && (
              <div className="mt-2 space-y-2">
                <a href="/about-us" className="flex items-center gap-2 px-3 py-2 rounded-lg dark:hover:bg-white/5 hover:bg-black/5 transition-colors text-white text-xs font-normal" onClick={() => setIsOpen(false)}>
                  <FontAwesomeIcon icon={faInfoCircle} className="h-4 w-4 text-white" /> {t('header.about_us')}
                </a>
                <a href="/careers" className="flex items-center gap-2 px-3 py-2 rounded-lg dark:hover:bg-white/5 hover:bg-black/5 transition-colors text-white text-xs font-normal" onClick={() => setIsOpen(false)}>
                  <FontAwesomeIcon icon={faBriefcase} className="h-4 w-4 text-white" /> {t('header.careers')}
                </a>
              </div>
            )}
          </div>

          {/* All Desktop Actions in Mobile */}
          <div className="flex flex-col gap-2 mb-4">
            {/* <a href="http://localhost:4001" className="flex items-center gap-3 px-4 py-2.5 text-sm font-normal dark:text-white text-gray-800 rounded-xl transition-all hover:bg-black/5 dark:hover:bg-white/10" onClick={() => setIsOpen(false)}>
              {t('header.api')}
            </a> */}
            <a href="https://support.oleyes.com" className="flex items-center gap-3 px-4 py-2.5 text-sm font-normal text-white rounded-xl transition-all hover:bg-black/5 dark:hover:bg-white/10" onClick={() => setIsOpen(false)}>
              {t('header.support')}
            </a>
            {/* <a href="https://billing.oleyes.com" className="flex items-center gap-3 px-4 py-2.5 text-sm font-normal dark:text-white text-gray-800 rounded-xl transition-all hover:bg-black/5 dark:hover:bg-white/10" onClick={() => setIsOpen(false)}>
              {t('header.billing')}
            </a> */}
            <a href="/contact-sales" className="flex items-center gap-3 px-4 py-2.5 text-sm font-normal text-white rounded-xl transition-all hover:bg-black/5 dark:hover:bg-white/10" onClick={() => setIsOpen(false)}>
              {t('header.contact_sales')}
            </a>
            {isLoggedIn && onDashboardClick && (
              <button
                onClick={() => {
                  onDashboardClick();
                  setIsOpen(false);
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 mt-2 text-sm font-medium text-white border border-white/70 rounded-lg transition-all duration-300 hover:bg-white/10 hover:scale-105"
              >
                {t('header.dashboard')}
              </button>
            )}

          </div>



        </div>
      </div>
    </header >
  );
}
